import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';

export interface ChartAccountOption {
  id: string;
  code: string;
  name: string;
}

interface ChartDeleteOptionsProps {
  /** Chart preparation mode; without it neither option is offered. */
  unlocked: boolean;
  /** The account still has child accounts. */
  hasChildren: boolean;
  /** The account already carries journal lines, so a merge target is required. */
  hasPostings: boolean;
  cascade: boolean;
  onCascadeChange: (value: boolean) => void;
  mergeTargetId: string | null;
  onMergeTargetChange: (value: string | null) => void;
  /** Candidate merge targets — the account being deleted is filtered out by the caller. */
  options: ChartAccountOption[];
}

const NONE = '__none__';

/**
 * The two operations an accountant needs while the chart is still being
 * prepared: drop a whole branch, and merge an account into another one (move
 * its postings, cari links, rules and running balance, then delete it).
 *
 * Shared by both charts, so its copy lives in the `common` namespace.
 */
export function ChartDeleteOptions({
  unlocked,
  hasChildren,
  hasPostings,
  cascade,
  onCascadeChange,
  mergeTargetId,
  onMergeTargetChange,
  options,
}: ChartDeleteOptionsProps) {
  const { t } = useTranslation('common');

  if (!unlocked) return null;

  return (
    <div className="space-y-4 rounded-md border p-3">
      <p className="text-xs text-amber-700">
        {t(
          'chartDelete.prepNotice',
          'Hesap planı hazırlık modu açık. Bu seçenekler üretimde kullanılamaz.'
        )}
      </p>

      {hasChildren && (
        <div className="flex items-start gap-2">
          <Checkbox
            id="cascade"
            checked={cascade}
            onCheckedChange={(value) => onCascadeChange(value === true)}
          />
          <div className="space-y-1">
            <Label htmlFor="cascade" className="text-sm font-normal">
              {t('chartDelete.cascade', 'Alt hesapları da sil')}
            </Label>
            <p className="text-muted-foreground text-xs">
              {t(
                'chartDelete.cascadeHelp',
                'İşaretlenmezse alt hesaplar silinmez; bir üst hesaba bağlanır.'
              )}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-normal">
          {t('chartDelete.mergeInto', 'Kayıtları şu hesaba aktar (birleştir)')}
        </Label>
        <Combobox
          options={[
            { value: NONE, label: t('chartDelete.noMerge', 'Aktarma — yalnızca sil') },
            ...options.map((option) => ({
              value: option.id,
              label: `${option.code} - ${option.name}`,
            })),
          ]}
          value={mergeTargetId ?? NONE}
          onValueChange={(value) => onMergeTargetChange(value === NONE ? null : value)}
          placeholder={t('chartDelete.selectMergeTarget', 'Hesap seçin')}
          searchPlaceholder={t('search', 'Ara...')}
        />
        <p className="text-muted-foreground text-xs">
          {hasPostings
            ? t(
                'chartDelete.mergeRequired',
                'Bu hesabın yevmiye kaydı var. Silmek için kayıtların aktarılacağı bir hesap seçmelisiniz.'
              )
            : t(
                'chartDelete.mergeHelp',
                'Yevmiye kayıtları, cari bağlantıları, entegrasyon tanımları ve bakiye seçilen hesaba taşınır.'
              )}
        </p>
      </div>
    </div>
  );
}
